// The moment the hall finds a partner. The same fighting-game splash the
// result screen stands on: both seats' Avatar art cut along the lightning
// crease, names in the corners, the agreed terms and one action along the
// bottom. Neutral: nobody has won anything yet. Both players must accept
// before the countdown runs out; after accepting, the seat waits on the
// partner. A four-seat pod lists the other three in the right corner and
// counts the ready seats instead. A draft / sealed offer is priced: the
// accept is the same pay control the DM invite carries, and the server
// charges the chosen currency the moment every seat has accepted.
import { Component } from 'preact';
import { cn } from '../lib/utils';
import {
  GOLD, ACCENT_GOLD, TEXT_BODY, TEXT_MUTED, PANEL_BG,
  GOLD_BTN, BEVELED_BTN, FourCorners,
} from '../lib/medievalTheme';
import { HEADER_TITLE_STYLE } from './play/ModeSetupDialog';
import MatchupBackdrop from './MatchupBackdrop';
import { resolveAvatarUrl } from '../utils/arena/avatarUtils';
import { formatRank, TIER_HEX } from '../utils/arena/rankUtils';
import { BOOSTER_SETS } from '../utils/arena/packsApi';
import { playUI, UI } from '../utils/arena/uiSounds';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import AcceptPayControl from './ui/AcceptPayControl';
import { k, t } from '../localization/t';

const MODE_LABEL = { ranked: k('Ranked'), casual: k('Casual'), sandbox: k('Sandbox'), draft: k('Draft'), sealed: k('Sealed') };
const BTN = { padding: '0.625cqw 1.5cqw', borderRadius: 'var(--radius-md)', transition: 'transform 160ms cubic-bezier(0.23, 1, 0.32, 1)' };

function rankOf(p) {
  const tier = p?.tier || p?.rank?.tier;
  const division = p?.division || p?.rank?.division;
  return tier ? { label: formatRank(tier, division), color: TIER_HEX[tier] || '#A6A09B' } : null;
}

export default class MatchFoundOverlay extends Component {
  state = { now: Date.now() };
  componentDidMount() {
    this._esc = pushDismiss(() => {});
    this._tick = setInterval(() => this.setState({ now: Date.now() }), 1000);
  }
  componentWillUnmount() {
    removeDismiss(this._esc);
    clearInterval(this._tick);
  }

  render() {
    const { match, profile, sorceryCards, onAccept, onDismiss } = this.props;
    const { kind, mode, opponent, format, rules, setKey, phase, opponentAccepted, expiresAt, entryCost, keystoneCost } = match;
    const opponents = match.opponents?.length ? match.opponents : (opponent ? [opponent] : []);
    const seats = match.seats || opponents.length + 1;
    const pod = opponents.length > 1;
    const acceptedIds = new Set(match.acceptedIds || []);
    const isEvent = kind === 'event';
    const paid = isEvent && entryCost > 0;
    const terms = [];
    if (mode === 'ranked' || mode === 'casual') terms.push(format === 'peasant' ? t('Peasant') : t('Constructed'));
    terms.push(t(MODE_LABEL[mode] || mode));
    if (isEvent && setKey) terms.push(BOOSTER_SETS[setKey]?.label || setKey);
    if (isEvent && pod) terms.push(t('{n} players', { n: seats }));
    if (rules) terms.push(rules === 'automated' ? t('Automated') : t('Manual'));
    const accepted = phase === 'accepted';
    const them = opponent?.name || t('Your opponent');
    const secondsLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - this.state.now) / 1000)) : null;
    // My own yes reaches me only through later seats' notices, so count it here.
    const readyCount = [...acceptedIds].filter((id) => id !== profile?.id).length + (accepted ? 1 : 0);
    const line = pod
      ? (accepted && opponentAccepted ? t('All ready. Starting.') : t('{ready} of {seats} ready', { ready: readyCount, seats }))
      : accepted
        ? (opponentAccepted ? t('Both ready. Starting.') : t('Waiting for {name}.', { name: them }))
        : (opponentAccepted ? t('{name} accepted.', { name: them }) : null);
    const first = opponents[0];
    const matchup = {
      localName: profile?.name || t('You'),
      remoteName: first?.name || t('Opponent'),
      localAvatarUrl: resolveAvatarUrl(profile, sorceryCards),
      remoteAvatarUrl: resolveAvatarUrl(first, sorceryCards),
    };
    const myRank = rankOf(profile);
    const theirRank = rankOf(first);
    const corners = [['left', matchup.localName, myRank], ...(pod ? [] : [['right', matchup.remoteName, theirRank]])];
    const NAME_SHADOW = '0 -1px 0 rgba(0,0,0,0.8), 0 1px 0 rgba(255,220,140,0.18), 0 2px 6px rgba(0,0,0,0.95)';

    return (
      <div className="fixed inset-0 z-[85] flex items-center justify-center" style={{ background: 'rgba(0, 0, 0, 0.78)', backdropFilter: 'blur(6px)' }}>
        <div
          className={cn('match-result-card match-result-frame relative w-[58cqw] h-[35cqw] rounded-2xl overflow-hidden')}
          style={{ background: PANEL_BG, isolation: 'isolate' }}
        >
          <MatchupBackdrop matchup={matchup} neutral headings={false} scrim={0.75} edgeScrim={1} />
          <div className="relative h-full p-6 flex flex-col">
            <FourCorners knots />

            {/* Each seat's name and rank in its corner, over its own art. */}
            {corners.map(([side, name, rank]) => (
              <div key={side} className={cn('absolute top-4 flex flex-col max-w-[40%]', side === 'left' ? 'left-5 items-start' : 'right-5 items-end')} style={{ gap: '0.2cqw' }}>
                <span className="arena-heading truncate max-w-full" style={{ fontSize: '1.2cqw', color: '#f4e4b8', textShadow: NAME_SHADOW }}>{name}</span>
                {rank ? <span className="text-[0.7cqw] font-bold" style={{ color: rank.color, textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>{rank.label}</span> : null}
              </div>
            ))}

            {/* A pod: the other seats stacked, each marked once it has accepted. */}
            {pod ? (
              <div className="absolute top-4 right-5 flex flex-col items-end max-w-[40%]" style={{ gap: '0.45cqw' }}>
                {opponents.map((o) => {
                  const rank = rankOf(o);
                  const ready = acceptedIds.has(o.id);
                  return (
                    <div key={o.id} className="flex flex-col items-end" style={{ gap: '0.1cqw' }}>
                      <span className="arena-heading truncate max-w-full" style={{ fontSize: '1cqw', color: ready ? ACCENT_GOLD : '#f4e4b8', textShadow: NAME_SHADOW }}>{ready ? `${o.name} ✓` : o.name}</span>
                      {rank ? <span className="text-[0.7cqw] font-bold" style={{ color: rank.color, textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>{rank.label}</span> : null}
                    </div>
                  );
                })}
              </div>
            ) : null}

            {/* Title on the crease. */}
            <div className="flex flex-col items-center" style={{ marginTop: '0.5cqw', gap: '0.35cqw' }}>
              <h2 className="match-result-title" style={{ ...HEADER_TITLE_STYLE, fontSize: '2.4cqw', textShadow: '0 2px 6px rgba(0,0,0,0.9), 0 0 24px rgba(212,168,67,0.25)' }}>
                {t('Match found')}
              </h2>
              <div className="flex items-center" style={{ gap: '0.5cqw' }}>
                {terms.map((term, i) => (
                  <span key={term} className="flex items-center" style={{ gap: '0.5cqw' }}>
                    {i > 0 ? <span className="rounded-full" style={{ width: '0.25cqw', height: '0.25cqw', background: `${GOLD} 0.6)` }} /> : null}
                    <span className="text-[0.72cqw] font-semibold" style={{ color: ACCENT_GOLD, letterSpacing: '0.04em', textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>{term}</span>
                  </span>
                ))}
              </div>
            </div>

            <div className="flex-1" />

            {line || secondsLeft !== null ? (
              <div className="text-[0.7cqw] text-center mb-3" style={{ color: TEXT_MUTED, textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
                {[line, secondsLeft !== null ? t('{s}s', { s: secondsLeft }) : null].filter(Boolean).join('  ·  ')}
              </div>
            ) : null}
            <div className="flex items-center justify-center" style={{ gap: '0.75cqw' }}>
              <button type="button" className="text-sm cursor-pointer active:scale-[0.97]" style={{ ...BEVELED_BTN, color: TEXT_BODY, ...BTN }} data-sound={UI.CANCEL} onClick={onDismiss}>
                {t('Decline')}
              </button>
              {!accepted && (paid ? (
                <AcceptPayControl
                  cost={entryCost}
                  ksCost={keystoneCost}
                  coins={profile?.coins || 0}
                  keystones={profile?.keystones || 0}
                  onAccept={onAccept}
                />
              ) : (
                <button type="button" className="text-sm font-semibold cursor-pointer active:scale-[0.97]" style={{ ...GOLD_BTN, ...BTN }} onClick={() => { playUI(UI.CONFIRM); onAccept(); }}>
                  {t('Accept')}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
